import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { testNameTokenization, runNameTokenizationBatch, processAllDocuments } from '../utils/nameTokenizationTester';

const TokenizationTest = () => {
  const { currentUser } = useAuth();
  const [documentId, setDocumentId] = useState('');
  const [batchSize, setBatchSize] = useState(50);
  const [startAfter, setStartAfter] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [isProcessingAll, setIsProcessingAll] = useState(false);
  const [progress, setProgress] = useState(null);

  // Authentication check
  if (!currentUser) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <i className="fas fa-exclamation-triangle text-yellow-400"></i>
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                You must be logged in to use this feature.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Test a single document
  const handleTestDocument = async (e) => {
    e.preventDefault();
    if (!documentId) {
      setError('Please enter a document ID');
      return;
    }

    setLoading(true);
    setError('');
    setResults(null);

    try {
      const result = await testNameTokenization(documentId);
      setResults(result);
    } catch (err) {
      setError(`Error: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  // Run a single batch
  const handleRunBatch = async (e) => {
    e.preventDefault();
    
    setLoading(true);
    setError('');
    setResults(null);

    try {
      const result = await runNameTokenizationBatch(
        parseInt(batchSize, 10),
        startAfter || null
      );
      setResults(result);
      
      // Auto-update the startAfter field if we have more documents
      if (!result.isComplete && result.lastDocumentId) {
        setStartAfter(result.lastDocumentId);
      }
    } catch (err) {
      setError(`Error: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  // Process all documents
  const handleProcessAll = async () => {
    setIsProcessingAll(true);
    setError('');
    setResults(null);
    setProgress({
      totalProcessed: 0,
      successful: 0,
      failed: 0,
      errors: [],
      isComplete: false
    });

    try {
      await processAllDocuments(parseInt(batchSize, 10), (progressData) => {
        setProgress(progressData);
      });
    } catch (err) {
      setError(`Error: ${err.message || 'Unknown error'}`);
    } finally {
      setIsProcessingAll(false);
    }
  };

  // Format JSON for display
  const formatJson = (json) => {
    return JSON.stringify(json, null, 2);
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-6">Name Tokenization Test</h1>
      
      {/* Warning for production use */}
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-8">
        <div className="flex">
          <div className="flex-shrink-0">
            <i className="fas fa-exclamation-triangle text-yellow-400"></i>
          </div>
          <div className="ml-3">
            <p className="text-sm text-yellow-700">
              <strong>Caution:</strong> This tool modifies your Firestore database. Test on a single document first before running in batch mode.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Test single document */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">Test Single Document</h2>
          <form onSubmit={handleTestDocument}>
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="documentId">
                Document ID
              </label>
              <input
                id="documentId"
                type="text"
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                value={documentId}
                onChange={(e) => setDocumentId(e.target.value)}
                placeholder="e.g. EU-1003"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className={`bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline ${
                loading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {loading ? 'Processing...' : 'Test Document'}
            </button>
          </form>
        </div>

        {/* Batch processing */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">Batch Processing</h2>
          <form onSubmit={handleRunBatch}>
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="batchSize">
                Batch Size
              </label>
              <input
                id="batchSize"
                type="number"
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                value={batchSize}
                onChange={(e) => setBatchSize(e.target.value)}
                min="1"
                max="500"
              />
            </div>
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="startAfter">
                Start After Document ID (Optional)
              </label>
              <input
                id="startAfter"
                type="text"
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                value={startAfter}
                onChange={(e) => setStartAfter(e.target.value)}
                placeholder="Leave empty to start from beginning"
              />
            </div>
            <div className="flex space-x-4">
              <button
                type="submit"
                disabled={loading || isProcessingAll}
                className={`bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline ${
                  (loading || isProcessingAll) ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {loading ? 'Processing...' : 'Run Batch'}
              </button>
              <button
                type="button"
                onClick={handleProcessAll}
                disabled={loading || isProcessingAll}
                className={`bg-purple-500 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline ${
                  (loading || isProcessingAll) ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {isProcessingAll ? 'Processing All...' : 'Process All Documents'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Error display */}
      {error && (
        <div className="mt-8 bg-red-50 border-l-4 border-red-500 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <i className="fas fa-times-circle text-red-500"></i>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Progress display */}
      {isProcessingAll && progress && (
        <div className="mt-8 bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">Processing Progress</h2>
          <div className="mb-4 bg-gray-200 rounded-full h-4">
            <div
              className="bg-blue-600 h-4 rounded-full transition-all duration-500 ease-in-out"
              style={{
                width: `${progress.isComplete ? 100 : Math.min(99, (progress.totalProcessed / (progress.totalProcessed + 100)) * 100)}%`,
              }}
            ></div>
          </div>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <p className="text-sm text-gray-500">Documents Processed</p>
              <p className="text-xl font-semibold">{progress.totalProcessed}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Success Rate</p>
              <p className="text-xl font-semibold">
                {progress.totalProcessed > 0
                  ? `${Math.round((progress.successful / progress.totalProcessed) * 100)}%`
                  : '0%'}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Successful</p>
              <p className="text-xl font-semibold text-green-600">{progress.successful}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Failed</p>
              <p className="text-xl font-semibold text-red-600">{progress.failed}</p>
            </div>
          </div>
          {progress.errors.length > 0 && (
            <div className="mt-4">
              <p className="text-sm font-semibold text-gray-700">Errors</p>
              <div className="mt-2 max-h-32 overflow-y-auto bg-gray-50 p-2 rounded text-xs font-mono">
                {progress.errors.map((err, i) => (
                  <div key={i} className="mb-1">{err}</div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Results display */}
      {results && (
        <div className="mt-8 bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">Results</h2>
          {results.documentId ? (
            <>
              <h3 className="text-lg font-medium mb-2">Document: {results.documentId}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="text-md font-medium mb-2">Before Tokenization</h4>
                  <p className="text-sm mb-1">Names: {results.before.namesCount}</p>
                  <div className="bg-gray-50 p-4 rounded overflow-auto max-h-96">
                    <pre className="text-xs">{formatJson(results.before.names)}</pre>
                  </div>
                </div>
                <div>
                  <h4 className="text-md font-medium mb-2">After Tokenization</h4>
                  <p className="text-sm mb-1">Names: {results.after.namesCount}</p>
                  <p className="text-sm mb-1">Unique Tokens: {results.after.nameTokensSet.length}</p>
                  <div className="bg-gray-50 p-4 rounded overflow-auto max-h-96">
                    <pre className="text-xs">{formatJson({
                      names: results.after.names,
                      nameTokensSet: results.after.nameTokensSet,
                      tokenToNameIndex: results.after.tokenToNameIndex
                    })}</pre>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <h3 className="text-lg font-medium mb-2">Batch Results</h3>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-sm text-gray-500">Documents Processed</p>
                  <p className="text-xl font-semibold">{results.totalProcessed}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Success Rate</p>
                  <p className="text-xl font-semibold">
                    {results.totalProcessed > 0
                      ? `${Math.round((results.successful / results.totalProcessed) * 100)}%`
                      : '0%'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Successful</p>
                  <p className="text-xl font-semibold text-green-600">{results.successful}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Failed</p>
                  <p className="text-xl font-semibold text-red-600">{results.failed}</p>
                </div>
              </div>
              {results.lastDocumentId && (
                <div className="mb-4">
                  <p className="text-sm text-gray-500">Last Document ID</p>
                  <p className="text-md font-mono">{results.lastDocumentId}</p>
                </div>
              )}
              {results.errors && results.errors.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm font-semibold text-gray-700">Errors</p>
                  <div className="mt-2 max-h-32 overflow-y-auto bg-gray-50 p-2 rounded text-xs font-mono">
                    {results.errors.map((err, i) => (
                      <div key={i} className="mb-1">{err}</div>
                    ))}
                  </div>
                </div>
              )}
              {!results.isComplete && (
                <div className="mt-4 p-3 bg-blue-50 rounded-md">
                  <p className="text-sm text-blue-700">
                    <i className="fas fa-info-circle mr-2"></i>
                    There are more documents to process. The "Start After" field has been updated with the last document ID for your convenience.
                  </p>
                </div>
              )}
              {results.isComplete && (
                <div className="mt-4 p-3 bg-green-50 rounded-md">
                  <p className="text-sm text-green-700">
                    <i className="fas fa-check-circle mr-2"></i>
                    All documents in this batch have been processed successfully!
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default TokenizationTest; 